/**
 * Run a callback once the page has finished loading.
 *
 * Used to keep genuinely non-critical work — decorative imagery, prompts —
 * out of the window that FCP, LCP and Time to Interactive are measured over,
 * without dropping the work altogether.
 *
 * Three cases, in order:
 *  - the page is already `complete` (a client-side navigation, or a fast load
 *    that beat this call) — run synchronously;
 *  - otherwise wait for `load`;
 *  - and back that with a timeout, because a single stalled subresource can
 *    hold `load` open indefinitely. For images that would mean permanently
 *    blank slots, which is worse than fetching them slightly early.
 *
 * Returns a cleanup function; the callback runs at most once.
 */
export function whenPageLoaded(callback: () => void, options?: { timeoutMs?: number }): () => void {
  // Guarded for the SSG prerender pass, which runs this module in Node.
  if (typeof window === 'undefined' || typeof document === 'undefined') return () => {};

  const timeoutMs = options?.timeoutMs ?? 5000;
  let done = false;
  // `timer` is assigned once below, but `run` and the returned cleanup both
  // close over it, so it cannot be a const initialised before they exist.
  // eslint-disable-next-line prefer-const
  let timer: number | undefined;

  const run = () => {
    if (done) return;
    done = true;
    window.removeEventListener('load', run);
    if (timer !== undefined) window.clearTimeout(timer);
    callback();
  };

  if (document.readyState === 'complete') {
    run();
    return () => {};
  }

  window.addEventListener('load', run, { once: true });
  timer = window.setTimeout(run, timeoutMs);

  return () => {
    done = true;
    window.removeEventListener('load', run);
    if (timer !== undefined) window.clearTimeout(timer);
  };
}
